import { useState } from "react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight, Download, ListChecks, Search } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { exportInvMovements } from "~/services/inventory/exportService";
import { inventoryOperationsService } from "~/services/inventory/operationsService";
import { formatCurrency, formatDate } from "~/utils/helpers";

import { NewOperationDialog } from "./components/NewOperationDialog";

const ESTADO: Record<string, { label: string; variant: "default" | "secondary" | "outline" }> = {
  draft: { label: "Borrador", variant: "secondary" },
  confirmed: { label: "Confirmado", variant: "secondary" },
  done: { label: "Contabilizado", variant: "default" },
  cancel: { label: "Anulado", variant: "outline" },
};

const formatQty = (n: number) =>
  new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n ?? 0);

export function InventoryOperationsPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["inv-movements", search],
    queryFn: () => inventoryOperationsService.getMovements(search),
  });
  const rows = data ?? [];

  const refetchAll = () => {
    queryClient.invalidateQueries({ queryKey: ["inv-movements"] });
    queryClient.invalidateQueries({ queryKey: ["inv-stock"] });
    queryClient.invalidateQueries({ queryKey: ["inv-pending"] });
  };

  return (
    <PageContainer>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span>
          Módulo de inventario <strong className="font-semibold">nuevo — en pruebas</strong>. Cada
          operación es un movimiento entre ubicaciones; el saldo es la suma de los movimientos.
        </span>
      </div>

      <PageHeader
        title="Inventario — Operaciones"
        description="Recepciones, entregas, traslados, ajustes y desechos (por planta)."
        actions={
          <>
            <Button
              variant="outline"
              className="h-9"
              disabled={rows.length === 0}
              onClick={() => exportInvMovements(rows)}
            >
              <Download className="mr-1.5 h-4 w-4" />
              Exportar Excel
            </Button>
            <NewOperationDialog onSuccess={refetchAll} />
          </>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por código o nombre…"
            className="h-9 pl-9"
          />
        </div>
      </div>

      {isLoading ? (
        <TableSkeleton rows={8} columns={9} />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="Sin movimientos"
          description="Aún no hay movimientos registrados en el nuevo inventario."
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Documento</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Producto</TableHead>
                    <TableHead>Movimiento</TableHead>
                    <TableHead className="text-right">Cantidad</TableHead>
                    <TableHead className="text-right">Valor (CLP)</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const est = ESTADO[r.estado] ?? { label: r.estado, variant: "outline" as const };
                    return (
                      <TableRow key={r.move_id}>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {formatDate(r.fecha)}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {r.documento ?? "-"}
                        </TableCell>
                        <TableCell>{r.tipo ?? "-"}</TableCell>
                        <TableCell className="font-medium">
                          {r.code ? <span className="text-muted-foreground">{r.code} · </span> : null}
                          {r.producto}
                        </TableCell>
                        <TableCell>
                          <span className="flex items-center gap-1 whitespace-nowrap text-sm text-muted-foreground">
                            {r.origen ?? "-"}
                            <ArrowRight className="h-3 w-3" />
                            {r.destino ?? "-"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {formatQty(r.cantidad)} {r.uom ?? ""}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {r.valor == null ? "-" : `$${formatCurrency(r.valor)}`}
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-muted-foreground">
                          {r.usuario ?? "-"}
                        </TableCell>
                        <TableCell>
                          <Badge variant={est.variant} className="font-normal">
                            {est.label}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </PageContainer>
  );
}

export default InventoryOperationsPage;
